import { Box, BoxProps } from '@mui/material';
import { generatePath, useNavigate } from 'react-router-dom';
import { useGetSignedInUser } from '../../hooks/useGetSignedInUser';
import { APP_URLS } from '../../routes/urls';
import { EligibilityFailResponse } from '../../store/features/apply/types';
import { StudentEducation } from '../../types/profile';
import { StrOrNum } from '../../types/util';
import { ModalError } from '../modal/ModalError';
import { EligibilityFailAccordionItem } from './EligibilityFailAccordionItem';
import { EligibilityItem, EligibilityContent, EligibilityContentResult } from './types';

interface EligibilityFailContentProps extends Required<Pick<EligibilityFailResponse, 'results'>> {
  eligibilityItems: EligibilityItem | EligibilityItem[];
  educations?: StudentEducation[];
  EmptyEducationContainerProps?: BoxProps;
  onCloseBtnClick?: () => void;
}
export const EligibilityFailContent: React.FC<EligibilityFailContentProps> = ({
  eligibilityItems,
  results,
  educations,
  onCloseBtnClick,
  EmptyEducationContainerProps,
}) => {
  const navigate = useNavigate();
  const { currentData: userData } = useGetSignedInUser();
  const _educations = educations || userData?.user?.educations;
  const navigateToEducationUpdate = (): void => {
    if (userData?.user) {
      navigate({ pathname: generatePath(APP_URLS.editEducationInfo, { studentSlug: userData.user.slug }) });
    }
  };

  if (!_educations || _educations.length === 0) {
    return (
      <Box {...EmptyEducationContainerProps}>
        <ModalError
          handleCancel={onCloseBtnClick}
          handleRetry={navigateToEducationUpdate}
          retryBtnLabel={userData?.user && 'Add Education Details'}
          cancelBtnLabel="Close"
          errorTitle="Oops.., it looks like you have not added your education details. Sorry you are not eligible to apply in this program or scholarship."
        />
      </Box>
    );
  }

  const getEducationById = (id: StrOrNum): StudentEducation | undefined => {
    if (!_educations) return;
    return _educations.find((education) => education.id.toString() === id.toString());
  };
  const getEligibilityFailContents = (): EligibilityContent[] => {
    const _eligibilityItems = Array.isArray(eligibilityItems) ? eligibilityItems : [eligibilityItems];
    return _eligibilityItems.map((eligibilityItem) => {
      const result = results.find(
        (resultItem) =>
          resultItem.id.toString() === eligibilityItem.id.toString() && resultItem.type === eligibilityItem.type
      );
      if (result) {
        return {
          id: eligibilityItem.id,
          title: eligibilityItem.name,
          includeTag: true,
          isEligible: result.isEligible,
          results: result.results.reduce((prevResults, resultItem) => {
            const education = getEducationById(resultItem.id);
            if (education) {
              prevResults.push({
                title: education.program.name,
                messages: resultItem.messages,
              });
            }
            return prevResults;
          }, [] as EligibilityContentResult[]),
        };
      } else {
        return {
          id: eligibilityItem.id,
          title: eligibilityItem.name,
          includeTag: true,
          isEligible: true,
        };
      }
    });
  };
  const eligibilityContents = getEligibilityFailContents();

  return (
    <>
      {eligibilityContents.map((eligibilityContent) => (
        <EligibilityFailAccordionItem
          key={`${eligibilityContent.title}${eligibilityContent.id}`}
          eligibilityContent={eligibilityContent}
        />
      ))}
    </>
  );
};
